import { useDeferredValue, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { faFileImport, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteDraftImport, fetchImports } from 'api/imports';
import * as DropdownMenu from 'components/dropdown-menu';
import { importFormatted } from 'components/imports/importStrategy';
import { useReactTableState } from 'components/table';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Import as TImport } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<TImport>();

const Drafts = () => {
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination, expanded, setExpanded } = useReactTableState();

    const queryParams = useDeferredValue({
        page: pagination.pageIndex + 1,
        drafts: true,
    });

    const { data: imports } = useSuspenseQuery({
        queryKey: accountQueryKeys.importsWithFilters(accountId, queryParams),
        queryFn: () => fetchImports(accountId, queryParams),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('resource', {
                header: 'Resource',
                cell: info => (
                    <div className={'flex flex-col justify-center'}>
                        <p className={'font-medium'}>{importFormatted(info.getValue())}</p>
                        {info.row.original.object_type !== 'account' && (
                            <p className={'text-gray-600'}>
                                {info.row.original.object_display_name}
                            </p>
                        )}
                    </div>
                ),
            }),

            columnHelper.accessor('created_at', {
                header: () => (
                    <div className={'flex items-baseline gap-1'}>
                        <p>Creation date</p>
                        <p className={'text-xs leading-5 text-gray-400'}>
                            ({DateTime.now().offsetNameShort})
                        </p>
                    </div>
                ),
                cell: info => (
                    <div>
                        {dateFromServer(info.getValue()).toLocaleString(DateTime.DATETIME_MED)}
                    </div>
                ),
            }),

            columnHelper.accessor('creator', {
                header: 'Created by',
                cell: info => (
                    <div className={'flex items-center gap-1 font-medium'}>
                        <p>{info.getValue()?.full_name}</p>
                    </div>
                ),
            }),

            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => <RowActions report={info.row.original} />,
                meta: {
                    ignoreClick: true,
                    className: 'flex items-center justify-end',
                },
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: imports?.data ?? [],
        columns,
        pageCount: imports?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            onRowClick: row => {
                history.push(`/accounts/${accountId}/settings/imports/drafts/${row.original.id}`);
            },
        },
    });

    return (
        <div>
            <Table.Root table={table} variant="bordered">
                <Box className={'flex flex-col overflow-hidden'}>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon>
                            <FontAwesomeIcon icon={faFileImport} />
                        </Table.EmptyIcon>
                        <Table.EmptyTitle>No drafts saved.</Table.EmptyTitle>
                    </Table.Empty>
                    <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </Box>
            </Table.Root>
        </div>
    );
};

const RowActions = ({ report }: { report: TImport }) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const deleteDraft = useMutation({
        mutationFn: (importId: number) => deleteDraftImport(accountId, importId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.imports(accountId) });
            toast.success('Draft deleted successfully.');
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message);
        },
    });

    return (
        <DropdownMenu.Root>
            <DropdownMenu.EllipsisTrigger className="float-right px-2 py-1" />
            <DropdownMenu.Content sideOffset={4} align={'end'}>
                <DropdownMenu.Item onClick={() => deleteDraft.mutate(report.id)}>
                    <div className={'flex items-center gap-x-2 text-red-600'}>
                        <FontAwesomeIcon className={'w-4'} icon={faTrash} />
                        Delete draft
                    </div>
                </DropdownMenu.Item>
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

export { Drafts };
